import {useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {ProjectItemSection$key} from './__generated__/ProjectItemSection.graphql'
import styles from './ProjectItemSection.module.css'
import {ProjectItemSectionFieldList} from './ProjectItemSectionFieldList'
import type {ProjectItemSectionViewFragment} from './ProjectItemSectionView'
import {ProjectItemSectionView} from './ProjectItemSectionView'

export type ProjectItemSectionProps = {
  projectItem: ProjectItemSection$key
  isOpen: boolean
  onIssueUpdate: () => void
  isProjectClosed: boolean
}

export function ProjectItemSection({
  projectItem,
  onIssueUpdate,
  isOpen = false,
  isProjectClosed,
}: ProjectItemSectionProps) {
  const [open, setOpen] = useState(isOpen)
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment ProjectItemSection on ProjectV2Item {
        id
        isArchived
        project {
          id
          title
          template
          viewerCanUpdate
          url
          field(name: "Status") {
            ... on ProjectV2SingleSelectField {
              databaseId
              id
              name
              options {
                id
                optionId
                name
                nameHTML
                color
                descriptionHTML
                # The status field has a non unique id. We leverage the getDataID method in the Relay environment,
                # and in this instance use the id, name, description and color to generate a unique ID:
                # - https://github.com/github/github/pull/305841/files#diff-7187f5ed804646bc353b5176a059d44bcd6a4cd5d5f19976aebb885d1e6997b8R155
                description
              }
            }
          }
        }
        fieldValueByName(name: "Status") {
          ... on ProjectV2ItemFieldSingleSelectValue {
            id
            optionId
            name
            nameHTML
            color
          }
        }
      }
    `,
    projectItem,
  )

  return (
    <div className={styles.ProjectItemSectionContainer}>
      <ProjectItemSectionView
        // RELAY-CLEANUP(ProjectsSectionFragment): ProjectItemSection remains Relay-owned, so cast to the
        // plain props fragment shape until the parent migration is complete.
        projectItem={data as ProjectItemSectionViewFragment}
        open={open}
        setOpen={setOpen}
        onIssueUpdate={onIssueUpdate}
        isSelectedProject={isOpen}
        isProjectClosed={isProjectClosed}
      />
      {open && (
        <ProjectItemSectionFieldList
          projectItemId={data.id}
          projectId={data.project.id}
          isArchived={data.isArchived}
          onIssueUpdate={onIssueUpdate}
        />
      )}
    </div>
  )
}
